import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { fetchSession, login, logout } from '../api/admin';
import { usePageMeta } from '../hooks/usePageMeta';
import CasesPanel from './CasesPanel';
import InsightsPanel from './InsightsPanel';
import LeadsPanel from './LeadsPanel';
import ServicesPanel from './ServicesPanel';
import { UnsavedChangesProvider, useUnsavedChanges } from './useUnsavedChanges';

type Tab = 'services' | 'cases' | 'insights' | 'leads';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'services', label: 'Tjänster' },
  { id: 'cases', label: 'Case' },
  { id: 'insights', label: 'Insights' },
  { id: 'leads', label: 'Förfrågningar' }
];

function LoginForm({ onLoggedIn }: { onLoggedIn: (username: string) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username, password);
      const session = await fetchSession();
      onLoggedIn(session.username);
    } catch {
      setError('Fel användarnamn eller lösenord.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-login">
      <p className="eyebrow">NorthBridge One</p>
      <h1>Innehållshantering</h1>
      <form className="form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="admin-username">Användarnamn</label>
          <input
            id="admin-username"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="admin-password">Lösenord</label>
          <input
            id="admin-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        {error && (
          <p className="form-status is-error" role="alert">
            {error}
          </p>
        )}
        <button className="button primary" type="submit" disabled={busy}>
          {busy ? 'Loggar in…' : 'Logga in'}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ username, onLogout }: { username: string; onLogout: () => void }) {
  const { confirmLeave } = useUnsavedChanges();
  const [tab, setTab] = useState<Tab>('services');

  return (
    <div className="admin">
      <header className="admin-bar">
        <div className="brand">
          <span className="mark">N</span>
          <span>
            NorthBridge<span className="sub"> CMS</span>
          </span>
        </div>
        <div className="admin-actions">
          <Link
            className="textlink"
            to="/"
            onClick={(event) => {
              if (!confirmLeave()) event.preventDefault();
            }}
          >
            Visa webbplatsen →
          </Link>
          <span className="meta">{username}</span>
          <button
            className="button secondary small"
            type="button"
            onClick={() => {
              if (!confirmLeave()) return;
              logout().then(onLogout);
            }}
          >
            Logga ut
          </button>
        </div>
      </header>

      <nav className="admin-tabs" aria-label="Innehållstyper">
        {TABS.map((item) => (
          <button
            key={item.id}
            className="chip"
            type="button"
            aria-pressed={tab === item.id}
            onClick={() => confirmLeave() && setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      <main className="admin-main">
        {tab === 'services' && <ServicesPanel />}
        {tab === 'cases' && <CasesPanel />}
        {tab === 'insights' && <InsightsPanel />}
        {tab === 'leads' && <LeadsPanel />}
      </main>
    </div>
  );
}

export default function AdminApp() {
  usePageMeta('Innehållshantering', 'Administration av innehållet på northbridgeone.se.');

  const [username, setUsername] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    fetchSession()
      .then((session) => setUsername(session.username))
      .catch(() => setUsername(null))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="admin-login">
        <div className="state" role="status">
          Kontrollerar inloggning…
        </div>
      </div>
    );
  }

  if (!username) {
    return <LoginForm onLoggedIn={setUsername} />;
  }

  return (
    <UnsavedChangesProvider>
      <Dashboard username={username} onLogout={() => setUsername(null)} />
    </UnsavedChangesProvider>
  );
}
